import { useState, type FormEvent, useEffect, useRef, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { WINE_COLORS, type WineColor } from '@gwc/shared';
import { winesApi, type WineCatalogInput } from '../api/wines';
import type { CatalogWineDetail } from '../api/models';
import { Combobox } from './Combobox';
import { ImageInput } from './ImageInput';
import { ApiError } from '../lib/api';

interface FormState {
  wineName: string;
  producer: string;
  varietals: string[];
  regionAva: string;
  country: string;
  vineyard: string;
  vintage: string;
  color: string;
  abv: string;
  typicalPrice: string;
  drinkFrom: string;
  drinkTo: string;
  labelImageUrl: string;
  tastingNotes: string;
  notes: string;
}

function emptyForm(): FormState {
  return {
    wineName: '',
    producer: '',
    varietals: [''],
    regionAva: '',
    country: '',
    vineyard: '',
    vintage: '',
    color: '',
    abv: '',
    typicalPrice: '',
    drinkFrom: '',
    drinkTo: '',
    labelImageUrl: '',
    tastingNotes: '',
    notes: '',
  };
}

function fromDetail(w: CatalogWineDetail & { id: string }): FormState {
  const s = (v: unknown) => (v == null ? '' : String(v));
  return {
    wineName: s(w.wineName),
    producer: s(w.producer),
    varietals: w.varietals.length ? w.varietals : [''],
    regionAva: s(w.region),
    country: s(w.country),
    vineyard: s(w.vineyard),
    vintage: s(w.vintage),
    color: s(w.color),
    abv: s(w.abv),
    typicalPrice: s(w.typicalPrice),
    drinkFrom: s(w.drinkFrom),
    drinkTo: s(w.drinkTo),
    labelImageUrl: s(w.labelImageUrl),
    tastingNotes: s(w.tastingNotes),
    notes: s(w.notes),
  };
}

const numOrNull = (v: string): number | null => {
  const n = Number(v.trim());
  return v.trim() === '' || Number.isNaN(n) ? null : n;
};

export function WineFormDialog({
  editing,
  onClose,
  onSaved,
}: {
  /** Pass an existing wine (with id) to edit, or null to create. */
  editing: (CatalogWineDetail & { id: string }) | null;
  onClose: () => void;
  onSaved: (id: string) => void;
}) {
  const qc = useQueryClient();
  const [f, setF] = useState<FormState>(editing ? fromDetail(editing) : emptyForm());
  const [error, setError] = useState<string | null>(null);
  const set = (patch: Partial<FormState>) => setF((prev) => ({ ...prev, ...patch }));

  const save = useMutation({
    mutationFn: async () => {
      const payload: WineCatalogInput = {
        wineName: f.wineName.trim(),
        producer: f.producer.trim() || undefined,
        varietals: f.varietals.map((v) => v.trim()).filter(Boolean),
        regionAva: f.regionAva.trim() || undefined,
        country: f.country.trim() || undefined,
        vineyard: f.vineyard.trim() || undefined,
        vintage: numOrNull(f.vintage),
        color: (f.color || null) as WineColor | null,
        abv: numOrNull(f.abv),
        typicalPrice: numOrNull(f.typicalPrice),
        drinkFrom: numOrNull(f.drinkFrom),
        drinkTo: numOrNull(f.drinkTo),
        labelImageUrl: f.labelImageUrl.trim() || undefined,
        tastingNotes: f.tastingNotes.trim() || undefined,
        notes: f.notes.trim() || undefined,
      };
      if (editing) {
        await winesApi.update(editing.id, payload);
        return editing.id;
      }
      return winesApi.create(payload);
    },
    onSuccess: (id) => {
      void qc.invalidateQueries({ queryKey: ['wines'] });
      void qc.invalidateQueries({ queryKey: ['wine', id] });
      onSaved(id);
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Could not save the wine'),
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!f.wineName.trim()) {
      setError('A wine name is required');
      return;
    }
    save.mutate();
  }

  const setVarietal = (i: number, v: string) =>
    setF((prev) => ({ ...prev, varietals: prev.varietals.map((x, j) => (j === i ? v : x)) }));

  const dialogRef = useRef<HTMLFormElement>(null);
  const titleId = 'wine-form-title';

  const onCloseRef = useCallback(onClose, [onClose]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialogRef.current?.querySelector<HTMLElement>('input,button,select,textarea')?.focus();

    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onCloseRef();
    }
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [onCloseRef]);

  return (
    <div
      className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-ink-950/70 p-4 backdrop-blur-sm"
      onMouseDown={onClose}
    >
      <form
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onSubmit={submit}
        onMouseDown={(e) => e.stopPropagation()}
        className="card my-8 w-full max-w-2xl space-y-4"
      >
        <div className="flex items-center justify-between">
          <h2 id={titleId} className="font-display text-xl text-cream-50">{editing ? 'Edit wine' : 'Add a wine'}</h2>
          <button type="button" onClick={onClose} className="text-sm text-cream-300/50 hover:text-cream-100">
            Close
          </button>
        </div>
        {error && <p className="text-sm text-wine-300">{error}</p>}

        <div>
          <label className="mb-1 block text-xs uppercase tracking-wide text-cream-300/50" htmlFor="wineName">
            Wine name
          </label>
          <input
            id="wineName"
            className="field"
            placeholder="e.g. Estate Cabernet Sauvignon"
            value={f.wineName}
            onChange={(e) => set({ wineName: e.target.value })}
            required
          />
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs uppercase tracking-wide text-cream-300/50" htmlFor="producer">
              Producer / winery
            </label>
            <Combobox id="producer" kind="producers" value={f.producer} onChange={(v) => set({ producer: v })} placeholder="Start typing…" />
          </div>
          <div>
            <label className="mb-1 block text-xs uppercase tracking-wide text-cream-300/50" htmlFor="vintage">
              Vintage
            </label>
            <input
              id="vintage"
              className="field"
              inputMode="numeric"
              placeholder="e.g. 2018 (blank = NV)"
              value={f.vintage}
              onChange={(e) => set({ vintage: e.target.value })}
            />
          </div>
        </div>

        <div>
          <label className="mb-1 block text-xs uppercase tracking-wide text-cream-300/50">Grapes (first is the primary)</label>
          <div className="space-y-2">
            {f.varietals.map((v, i) => (
              <div key={i} className="flex items-center gap-2">
                <Combobox kind="varietals" value={v} onChange={(val) => setVarietal(i, val)} placeholder={i === 0 ? 'Primary grape' : 'Blend grape'} />
                {f.varietals.length > 1 && (
                  <button
                    type="button"
                    onClick={() => set({ varietals: f.varietals.filter((_, j) => j !== i) })}
                    className="shrink-0 text-cream-300/40 hover:text-wine-300"
                    aria-label="Remove grape"
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={() => set({ varietals: [...f.varietals, ''] })}
            className="mt-2 text-xs text-gold-300 hover:text-gold-200"
          >
            + Add grape
          </button>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs uppercase tracking-wide text-cream-300/50" htmlFor="color">
              Colour / style
            </label>
            <select id="color" className="field capitalize" value={f.color} onChange={(e) => set({ color: e.target.value })}>
              <option value="">—</option>
              {WINE_COLORS.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs uppercase tracking-wide text-cream-300/50" htmlFor="abv">
              ABV %
            </label>
            <input id="abv" className="field" inputMode="decimal" placeholder="e.g. 14.5" value={f.abv} onChange={(e) => set({ abv: e.target.value })} />
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs uppercase tracking-wide text-cream-300/50" htmlFor="regionAva">
              Region / appellation
            </label>
            <Combobox id="regionAva" kind="regions" value={f.regionAva} onChange={(v) => set({ regionAva: v })} placeholder="e.g. Napa Valley" />
          </div>
          <div>
            <label className="mb-1 block text-xs uppercase tracking-wide text-cream-300/50" htmlFor="country">
              Country
            </label>
            <input id="country" className="field" placeholder="e.g. USA" value={f.country} onChange={(e) => set({ country: e.target.value })} />
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs uppercase tracking-wide text-cream-300/50" htmlFor="vineyard">
              Vineyard
            </label>
            <input id="vineyard" className="field" value={f.vineyard} onChange={(e) => set({ vineyard: e.target.value })} />
          </div>
          <div>
            <label className="mb-1 block text-xs uppercase tracking-wide text-cream-300/50" htmlFor="price">
              Typical price ($)
            </label>
            <input id="price" className="field" inputMode="decimal" placeholder="e.g. 45" value={f.typicalPrice} onChange={(e) => set({ typicalPrice: e.target.value })} />
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs uppercase tracking-wide text-cream-300/50" htmlFor="drinkFrom">
              Drink from (year)
            </label>
            <input id="drinkFrom" className="field" inputMode="numeric" value={f.drinkFrom} onChange={(e) => set({ drinkFrom: e.target.value })} />
          </div>
          <div>
            <label className="mb-1 block text-xs uppercase tracking-wide text-cream-300/50" htmlFor="drinkTo">
              Drink by (year)
            </label>
            <input id="drinkTo" className="field" inputMode="numeric" value={f.drinkTo} onChange={(e) => set({ drinkTo: e.target.value })} />
          </div>
        </div>

        <div>
          <label className="mb-1 block text-xs uppercase tracking-wide text-cream-300/50" htmlFor="labelImageUrl">
            Label image
          </label>
          <ImageInput id="labelImageUrl" value={f.labelImageUrl} onChange={(url) => set({ labelImageUrl: url })} />
        </div>

        <div>
          <label className="mb-1 block text-xs uppercase tracking-wide text-cream-300/50" htmlFor="tastingNotes">
            Tasting notes
          </label>
          <textarea id="tastingNotes" className="field min-h-[80px]" value={f.tastingNotes} onChange={(e) => set({ tastingNotes: e.target.value })} />
        </div>

        <div>
          <label className="mb-1 block text-xs uppercase tracking-wide text-cream-300/50" htmlFor="notes">
            Internal notes
          </label>
          <textarea id="notes" className="field min-h-[64px]" value={f.notes} onChange={(e) => set({ notes: e.target.value })} />
        </div>

        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="btn-ghost">
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : editing ? 'Save changes' : 'Add to Cellar'}
          </button>
        </div>
      </form>
    </div>
  );
}
